import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, Check, X } from "@phosphor-icons/react"
import { Command as CommandPrimitive } from "cmdk"
import { Button } from "@/components/ui/button"
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command"
import { FormError } from "@/components/ui/form"
import { inputVariant } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { useClickOutside } from "@/hooks/useClickOutside"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"

interface GroupBase<Option> {
  readonly options: readonly Option[]
  readonly heading?: string
}

interface MultipleSelectGroupedProps<Option, Group extends GroupBase<Option>> {
  groups: Group[]
  options?: never
}

interface MultipleSelectUngroupedProps<Option> {
  options: Option[]
  groups?: never
}

interface MultipleSelectCommonProps<Option> {
  label: string
  value?: Option[]
  error?: string
  clearable?: boolean
  filterable?: boolean
  popoverModal?: boolean
  translateError?: boolean
  hasDisplayDescriptionOnHover?: boolean
  scrollAreaClassName?: React.HTMLAttributes<HTMLDivElement>["className"]
  innerScrollClassName?: React.HTMLAttributes<HTMLDivElement>["className"]
  "data-testid"?: string
  getOptionLabel: (option: Option) => string
  getOptionValue: (option: Option) => string | number
  getExtraText?: (option: Option) => string
  getOptionDescription?: (option: Option) => string | undefined
  isOptionSelected?: (option: Option, selectedValue: Option[]) => boolean
  onChange?: (value: Option[]) => void
  renderSelectedOptions?: (options: Option[], toggleOption: (option: Option) => void) => React.ReactElement
}

type MultipleSelectProps<Option, Group extends GroupBase<Option>> = (
  | (MultipleSelectGroupedProps<Option, Group> & MultipleSelectCommonProps<Option>)
  | (MultipleSelectUngroupedProps<Option> & MultipleSelectCommonProps<Option>)
) &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">

const MultipleSelect = <Option, Group extends GroupBase<Option> = GroupBase<Option>>(
  props: MultipleSelectProps<Option, Group>,
) => {
  const {
    label,
    value,
    required,
    translateError,
    options,
    groups,
    error,
    clearable,
    filterable = true,
    popoverModal = true,
    hasDisplayDescriptionOnHover = false,
    scrollAreaClassName,
    innerScrollClassName,
    "data-testid": dataTestId,
    className,
    onChange,
    getOptionLabel,
    getOptionValue,
    getExtraText,
    getOptionDescription,
    isOptionSelected,
    renderSelectedOptions,
    ...rest
  } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [internalValue, setInternalValue] = useState<Option[]>(value ?? [])
  const [stringValue, setStringValue] = useState<string>("")
  const { t } = useTranslation()

  useClickOutside((event: MouseEvent) => {
    if (isOpen && resultRef.current && !resultRef.current.contains(event.target as HTMLElement)) {
      setIsOpen(false)
    }
  }, inputRef)

  const _getOptionLabel = useCallback((option: Option) => getOptionLabel(option), [getOptionLabel])
  const _getOptionValue = useCallback((option: Option) => getOptionValue(option), [getOptionValue])
  const _getExtraText = useCallback((option: Option) => getExtraText?.(option), [getExtraText])
  const _getOptionDescription = useCallback((option: Option) => getOptionDescription?.(option), [getOptionDescription])

  const isInternalOptionSelected = (option: Option): boolean => {
    if (internalValue.includes(option)) return true

    const candidate = _getOptionValue(option)

    return internalValue.some((i) => getOptionValue(i) === candidate)
  }

  const _isOptionSelected = (option: Option) =>
    isOptionSelected ? isOptionSelected(option, internalValue) : isInternalOptionSelected(option)

  const handleClear = () => {
    setStringValue("")
  }

  const toggleOption = (value: Option) => {
    const newSelectedValues = internalValue.includes(value)
      ? internalValue.filter((v) => v !== value)
      : [...internalValue, value]
    setInternalValue(newSelectedValues)
    onChange?.(newSelectedValues)
  }

  const renderOption = (option: Option) => {
    const isSelected = _isOptionSelected(option)
    const label = _getOptionLabel(option)
    const value = _getOptionValue(option)
    const extraText = _getExtraText(option)
    const description = _getOptionDescription(option)

    const optionContent = (
      <CommandItem
        key={value}
        data-testid={dataTestId && normalizeTestId(dataTestId, "-item")}
        className={cn(
          "my-0.5 flex cursor-pointer flex-col items-start rounded-md break-all has-[span]:pr-10",
          isSelected && "bg-primary-surface! text-on-primary-surface",
        )}
        onSelect={() => {
          toggleOption(option)
        }}>
        <p>{label}</p>
        {extraText && <p className="text-xs">{extraText}</p>}
        {isSelected && (
          <span className="absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center">
            <Check className="text-on-primary-surface" />
          </span>
        )}
      </CommandItem>
    )

    if (hasDisplayDescriptionOnHover && description) {
      return (
        <TooltipCustom key={value}>
          <TooltipTrigger asChild>
            <div className="grid">{optionContent}</div>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent align="start" side="bottom">
              {description}
            </TooltipContent>
          </TooltipPortal>
        </TooltipCustom>
      )
    }

    return optionContent
  }

  const renderGroupedOptions = () => {
    return groups?.map((group, idx) => {
      const key = idx
      return (
        <CommandGroup
          key={key}
          heading={group.heading}
          data-testid={dataTestId && normalizeTestId(dataTestId, "-group")}
          className="p-2">
          {group.options.map((item) => {
            return renderOption(item)
          })}
        </CommandGroup>
      )
    })
  }

  const renderUngroupedOptions = () => {
    return <div className="px-2">{options?.map(renderOption)}</div>
  }

  const renderOptions = () => {
    if (groups) {
      return renderGroupedOptions()
    } else if (options) {
      return renderUngroupedOptions()
    }
  }

  const [shouldFilter, setShouldFilter] = useState(false)

  const hasAnyOption = groups ? groups.some((group) => group.options.length > 0) : (options?.length ?? 0) > 0

  return (
    <div>
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={popoverModal}>
        <CommandPrimitive
          className={cn("relative", className)}
          shouldFilter={shouldFilter}
          data-testid={dataTestId && normalizeTestId(dataTestId)}>
          <PopoverTrigger asChild>
            <div className="relative">
              <CommandPrimitive.Input
                {...rest}
                className={cn(
                  inputVariant(),
                  "focus:placeholder:text-on-surface-subdued disabled:bg-surface-disabled pr-20 placeholder:text-transparent",
                  error && "outline-critical! outline-1 focus:outline-2",
                )}
                placeholder=" "
                value={stringValue}
                data-testid={dataTestId && normalizeTestId(dataTestId, "-input")}
                onValueChange={(search) => {
                  setStringValue(search)
                  if (filterable) setShouldFilter(true)
                  if (!isOpen) setIsOpen(true)
                }}
                ref={inputRef}
                onClick={() => {
                  if (!isOpen) setIsOpen(true)
                  if (filterable) setShouldFilter(false)
                }}
              />
              <label
                className={cn(
                  "text-on-surface-subdued peer-focus:text-primary pointer-events-none absolute top-1 left-4 cursor-text text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
                  error && "text-critical peer-placeholder-shown:text-on-surface-subdued peer-focus:text-critical",
                )}>
                {label}
                {required && "*"}
              </label>
              <div className="pointer-events-none absolute top-6 right-1 -translate-y-1/2 p-2">
                <CaretDown className="text-on-surface-subdued h-6 w-6" />
              </div>
              {clearable && stringValue && (
                <Button
                  variant="unstyle"
                  data-testid={dataTestId && normalizeTestId(dataTestId, "-clear-button")}
                  className="hover:bg-surface-hovered absolute top-6 right-10 -translate-y-1/2 px-2.5"
                  onClick={(e) => {
                    e.preventDefault()
                    handleClear()
                  }}>
                  <X className="text-on-surface-subdued size-5" />
                </Button>
              )}
            </div>
          </PopoverTrigger>
          {error && <FormError> {translateError ? t(error) : error}</FormError>}
          <PopoverContent
            ref={resultRef}
            className="bg-surface shadow-4 z-50 w-(--radix-popper-anchor-width) rounded-xl p-0 outline-hidden"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}>
            <CommandList className="max-h-none px-0">
              {hasAnyOption ? (
                <CommandEmpty
                  data-testid={dataTestId && normalizeTestId(dataTestId, "-empty")}
                  className="px-4 pt-4 pb-0.5 text-sm">
                  {t("common.message.noResultFound")}
                </CommandEmpty>
              ) : (
                <p
                  data-testid={dataTestId && normalizeTestId(dataTestId, "-empty")}
                  className="px-4 pt-4 pb-0.5 text-center text-sm">
                  {t("common.message.noResultFound")}
                </p>
              )}
              <ScrollArea viewportClassName={scrollAreaClassName}>
                <div className={cn("py-2", groups && "divide-y", innerScrollClassName)}>{renderOptions()}</div>
              </ScrollArea>
            </CommandList>
          </PopoverContent>
        </CommandPrimitive>
      </Popover>
      {renderSelectedOptions ? (
        renderSelectedOptions(internalValue, toggleOption)
      ) : (
        <ul
          className="mt-2 inline-flex w-full flex-wrap gap-2"
          data-testid={dataTestId && normalizeTestId(dataTestId, "-selected-list")}>
          {internalValue.map((value) => {
            const id = getOptionValue(value)
            const label = getOptionLabel(value)
            return (
              <li
                key={id}
                className="border-border inline-flex items-center space-x-2 rounded-md border px-3 py-0.5 break-all"
                data-testid={dataTestId && normalizeTestId(dataTestId, "-selected-item")}>
                <span className="text-sm">{label}</span>
                <X
                  className="text-on-surface-subdued size-4 shrink-0 cursor-pointer transition-transform hover:scale-125"
                  data-testid={dataTestId && normalizeTestId(dataTestId, "-remove-item")}
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleOption(value)
                  }}
                />
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export { MultipleSelect }
